import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthService } from '@auth/auth.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { UserRegistrationService } from 'src/app/modules/registration/services/user-registration.service';
import { MatDialog } from '@angular/material/dialog';
import { HipaaCancelWarningDialogComponent } from './dialogs/cancel-warning/hipaa-cancel-warning-dialog.component';
import { HipaaLoadErrorDialogComponent } from 'src/app/shared/components/hipaa-load-error-dialog/hipaa-load-error-dialog.component';
import DOMPurify from 'dompurify';

@Component({
  selector: 'mpp-hipaa-consent',
  templateUrl: './hipaa-consent.component.html',
  styleUrls: ['./hipaa-consent.component.scss'],
  standalone: false,
})
export class HipaaConsentComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  public consentContent: SafeHtml;
  public isLoadingContent = true;
  public contentLoadError = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private spinnerService: SpinnerService,
    private analyticsService: AnalyticsService,
    private userRegistrationService: UserRegistrationService,
    private dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    const contentUrl = this.route.snapshot.queryParamMap.get('contentUrl');

    if (contentUrl) {
      this.loadConsentContent(contentUrl);
    } else {
      this.handleContentLoadError(new Error('No HIPAA consent url'));
    }

    this.analyticsService.logScreenViewWeb('screen_hipaa_consent');
  }

  private loadConsentContent(url: string): void {
    this.isLoadingContent = true;
    this.contentLoadError = false;

    // Signal the web platform to the backend so it returns JSON { consentUrl } instead of a cross-origin 302 redirect (the mobile path), which the browser can't follow due to CORS.
    // Sent as a query param rather than an RMDHandsetPlatform header to avoid a CORS preflight.
    let backendUrl: URL;
    try {
      backendUrl = new URL(url);
      backendUrl.searchParams.set('platform', 'Web');
    } catch (error) {
      // new URL() throws synchronously on a malformed/relative url, outside the observable's error handler
      this.handleContentLoadError(error);
      return;
    }

    // Backend returns JSON { consentUrl: "https://..." } for web platform (HYPR-8417)
    this.http
      .get<{ consentUrl: string }>(backendUrl.toString())
      .pipe(
        switchMap((response) => {
          if (!response.consentUrl) {
            throw new Error('No consentUrl in response');
          }
          return this.http.get(response.consentUrl, { responseType: 'text' });
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (html) => {
          const sanitizedHtml = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
          this.consentContent = this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
          this.isLoadingContent = false;
        },
        (error) => {
          this.handleContentLoadError(error);
        },
      );
  }

  // AC MWEB-1787.7.1: during onboarding, show an alert and default the user to HIPAA declined.
  private handleContentLoadError(error: unknown): void {
    console.error('[HipaaConsentComponent] Failed to load consent content:', error);
    this.isLoadingContent = false;
    this.contentLoadError = true;

    this.dialog
      .open(HipaaLoadErrorDialogComponent, { disableClose: true })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.onDecline());
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    // Cancel button shows warning dialog
    this.showCancelWarningDialog();
  }

  private showCancelWarningDialog(): void {
    const dialogRef = this.dialog.open(HipaaCancelWarningDialogComponent, {
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async (shouldLogout: boolean) => {
      if (shouldLogout) {
        // User chose to log out
        const spinner = this.spinnerService.show();
        this.analyticsService.logLoggedOutEvent();
        await this.authService.logout();
        spinner.hide();
      }
      // If shouldLogout is false, user wants to stay - do nothing
    });
  }

  public onAccept(): void {
    const spinner = this.spinnerService.show();

    this.userRegistrationService.acceptHipaaConsent().subscribe((response) => {
      spinner.hide();

      if (response.result?.success) {
        this.router.navigate(['/dashboard']);
      } else {
        console.error('[HipaaConsentComponent] Accept failed:', response.result);
      }
      // Note: Error handling is done by the service's showError pipe
    });
  }

  public onDecline(): void {
    const spinner = this.spinnerService.show();

    this.userRegistrationService.declineHipaaConsent().subscribe((response) => {
      spinner.hide();

      if (response.result?.success) {
        this.router.navigate(['/dashboard']);
      } else {
        console.error('[HipaaConsentComponent] Decline failed:', response.result);
      }
      // Note: Error handling is done by the service's showError pipe
    });
  }
}
